import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useIntl } from 'react-intl';

import { Button, Checkbox, FormControlLabel, Grid, IconButton, List, ListItem, ListItemText } from '@mui/material';
import { Icon, PhoneNumberField, SimpleTextField, useSettings } from 'app/main/components';
import { type ModelType } from 'app/main/components/hooks/useModel';

interface EditListItemProps {
	index: number;
	module: string;
	item: any;
	sendChange: (index: number, name: string, newValue: string | number | boolean | null) => void;
	deleteItem: (index: number) => void;
}

interface DetailsSectionContactsProps {
	module: string;
	listField: string;
	list?: any[];
	sendChange: ModelType['sendChange'];
}

function EditListItem(props: EditListItemProps) {
	const { settings, t } = useSettings(props.module);
	const intl = useIntl();

	const handleChange = (name, value) => props.sendChange(props.index, name, value);

	return (
		<ListItem
			className="mb-8 py-0 pl-0"
			secondaryAction={
				<IconButton onClick={() => props.deleteItem(props.index)} edge="end">
					<Icon icon="delete" />
				</IconButton>
			}
		>
			<ListItemText
				className="m-0"
				primary={
					<Grid container spacing={1}>
						<Grid size={{ md: 5, xs: 12 }}>
							{props.module === 'mails' ? (
								<SimpleTextField
									name={`${settings.modu_short}_title`}
									label={intl.formatMessage(
										{
											id: `${props.module}ContactSection.title`,
											defaultMessage: t(`DETAILS_${props.module}_FIELD_TITLE_INDEX`)
										},
										{ index: props.index + 1 }
									)}
									value={props.item[`${settings.modu_short}_title`]}
									isEdit
									sendChange={handleChange}
									required
									validations={{ isEmail: true }}
									validationErrors={{
										isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED'),
										isEmail: t('ERROR_TEXT_EMAIL')
									}}
								/>
							) : (
								<PhoneNumberField
									name={`${settings.modu_short}_title`}
									label={intl.formatMessage(
										{
											id: `${props.module}ContactSection.title`,
											defaultMessage: t(`DETAILS_${props.module}_FIELD_TITLE_INDEX`)
										},
										{ index: props.index + 1 }
									)}
									value={props.item[`${settings.modu_short}_title`]}
									isEdit
									sendChange={handleChange}
								/>
							)}
						</Grid>
						<Grid size={{ md: 5, xs: 12 }}>
							<SimpleTextField
								name={`${settings.modu_short}_description`}
								label={intl.formatMessage(
									{
										id: `${props.module}ContactSection.description`,
										defaultMessage: t(`DETAILS_${props.module}_FIELD_DESCRIPTION_INDEX`)
									},
									{ index: props.index + 1 }
								)}
								value={props.item[`${settings.modu_short}_description`]}
								isEdit
								sendChange={handleChange}
							/>
						</Grid>
						<Grid size={{ md: 2, xs: 12 }}>
							<FormControlLabel
								className="m-4"
								label={t(`DETAILS_${props.module}_FIELD_DEFAULT`)}
								control={
									<Checkbox
										name={`${settings.modu_short}_default`}
										checked={props.item[`${settings.modu_short}_default`] || false}
										onChange={event => handleChange(event.target.name, event.target.checked)}
									/>
								}
							/>
						</Grid>
					</Grid>
				}
			/>
		</ListItem>
	);
}

/** This section is used by the FastAddDialog to create new contacts */
export default function DetailsSectionContacts(props: DetailsSectionContactsProps) {
	const { settings, t } = useSettings(props.module);

	const [currentList, setCurrentList] = React.useState(props.list || []);

	const addItem = () => {
		setCurrentList(state => {
			const newState = [...(state || []), { [`${settings.modu_short}_uid`]: uuidv4() }];
			props.sendChange(props.listField, [...newState]);
			return [...newState];
		});
	};

	const handleChange = (index: number, name: string, newValue: string | number | boolean | null) => {
		setCurrentList(state => {
			// If this field is the "default" field resets others
			if (name === `${settings.modu_short}_default` && newValue) {
				state.forEach((item, i) => {
					item[`${settings.modu_short}_default`] = false;
					state[i] = item;
				});
			}

			// Set new field value in the correct record
			state[index] = { ...state[index], [name]: newValue };

			props.sendChange(props.listField, state);
			return [...state];
		});
	};

	const deleteItem = (index: number) => {
		setCurrentList(state => {
			state.splice(index, 1);
			props.sendChange(props.listField, [...state]);
			return [...state];
		});
	};

	return (
		<Grid size={12}>
			<Grid container direction="column">
				{currentList?.length > 0 && (
					<Grid size={12}>
						<List dense disablePadding>
							{currentList?.map((item, key) => (
								<EditListItem
									key={key}
									index={key}
									module={props.module}
									item={item}
									sendChange={handleChange}
									deleteItem={deleteItem}
								/>
							))}
						</List>
					</Grid>
				)}
				<Grid size={12}>
					<Button startIcon={<Icon icon="add-circle" />} onClick={addItem}>
						{t(`DIALOG_${props.module}_ADD`)}
					</Button>
				</Grid>
			</Grid>
		</Grid>
	);
}
